import React, { useState, useRef, useEffect } from 'react';
import { useTooltips } from '../contexts/TooltipContext';
import { VideoLessonModal } from './VideoLessonModal';
import { HelpCircle, ExternalLink, PlayCircle, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface TooltipHelpProps {
  id: string;
  align?: 'left' | 'right';
}

export const TooltipHelp: React.FC<TooltipHelpProps> = ({ id, align = 'left' }) => {
  const { getTooltipById } = useTooltips();
  const { theme } = useTheme();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const tooltip = getTooltipById(id);

  // Fecha o popover se o usuário clicar fora dele
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsPopoverOpen(false);
      }
    }
    
    if (isPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopoverOpen]);

  if (!tooltip || !tooltip.ativo) return null;

  const hasVideo = tooltip.videoPlataforma && tooltip.videoUrl && tooltip.videoPlataforma !== 'none';
  const hasSupportLink = tooltip.linkSuporte && tooltip.linkSuporte.trim().length > 0;

  return (
    <div className="relative inline-flex items-center" ref={containerRef}>
      {/* Ícone de interrogação */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsPopoverOpen(!isPopoverOpen);
        }}
        className="p-1 rounded-full transition-all focus:outline-none hover:scale-105"
        style={{
          color: theme === 'dark' ? '#9ca3af' : '#6b7280'
        }}
        aria-label={`Ajuda sobre ${tooltip.titulo}`}
      >
        <HelpCircle className="w-5 h-5 cursor-pointer hover:text-blue-500 transition-colors" />
      </button>

      {/* Popover */}
      {isPopoverOpen && (
        <div 
          className={`absolute z-40 top-full mt-2 w-72 p-4 rounded-xl shadow-xl border animate-fade-in text-left cursor-default select-none ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Botão fechar (X) */}
          <button
            type="button"
            onClick={() => setIsPopoverOpen(false)}
            className="absolute top-3 right-3 p-1 rounded-md transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Fechar ajuda"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Conteúdo do texto */}
          <div className="pr-4">
            <p 
              className="text-sm font-normal leading-relaxed mb-4"
              style={{ color: 'var(--text-secondary)' }}
            >
              {tooltip.mensagem}
            </p>
          </div>

          {/* Links e Vídeos */}
          {(hasVideo || hasSupportLink) && (
            <div className="flex flex-col gap-2 border-t pt-3" style={{ borderColor: 'var(--border-color-soft)' }}>
              {hasVideo && (
                <button
                  type="button"
                  onClick={() => {
                    setIsPopoverOpen(false);
                    setIsVideoModalOpen(true);
                  }}
                  className="flex items-center gap-2 text-blue-500 hover:text-blue-600 text-xs font-semibold bg-transparent border-0 p-0 text-left cursor-pointer transition-colors"
                >
                  <PlayCircle className="w-4 h-4" />
                  Assistir videoaula
                </button>
              )}

              {hasSupportLink && (
                <a
                  href={tooltip.linkSuporte || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-500 hover:text-blue-600 text-xs font-semibold decoration-none transition-colors"
                >
                  Saiba mais
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal de vídeo */}
      {isVideoModalOpen && (
        <VideoLessonModal
          isOpen={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          tooltip={tooltip}
        />
      )}
    </div>
  );
};
